/**
 * Modelos y contratos de integración para el módulo de Entrenadores (Coaches)
 * Contrato: coaches-openapi.yaml y match-engine-service-openapi.yaml
 */

import { RealClubInfo } from './types';

export interface CoachListing {
  realClub?: RealClubInfo | null;
  club?: string | null;
  id: string; // UUID del entrenador
  name: string;
  image_url: string | null;
  attack: number; // Porcentaje directo, ej. 4 = +4%
  midfield: number; // ej. 3 = +3%
  defense: number; // ej. 8 = +8% (la suma de attack+midfield+defense es 15%)
  updated_at?: string;
}

export interface CoachMarket {
  id?: string;
  group_id?: string;
  season_id?: string;
  market_date?: string;
  closes_at?: string; // ISO date-time del cierre diario (Europe/Madrid)
  minimum_bonus?: number; // Prima mínima exigida (ej. 1.000.000 €)
  status?: string; // 'OPEN' | 'CLOSED' | string
  listings: CoachListing[];
}

export interface CoachContract {
  realClub?: RealClubInfo | null;
  attack?: number;
  midfield?: number;
  defense?: number;
  id?: string;
  club_id?: string;
  group_id?: string;
  season_id?: string;
  coach_id: string;
  name: string;
  image_url?: string | null;
  annual_salary: number; // Salario de temporada en EUR
  signing_bonus: number; // Prima de fichaje pagada en EUR
  release_clause: number; // Cláusula de rescisión informativa
  first_round: number; // Primera jornada contractual
  total_rounds: number; // Total de jornadas de la temporada
  signed_at: string;
  ends_at?: string | null;
  terminated_at?: string | null;
  active: boolean;
}

export type CoachOfferStatus =
  | 'PENDING'
  | 'WON'
  | 'LOST'
  | 'INSUFFICIENT_FUNDS'
  | 'CANCELLED'
  | string;

export interface CoachOffer {
  name?: string | null;
  image_url?: string | null;
  club?: string | null;
  realClub?: RealClubInfo | null;
  id: string; // UUID de la oferta suministrado por el cliente
  market_id: string;
  coach_id: string;
  club_id?: string;
  signing_bonus: number;
  annual_salary: number;
  release_clause: number;
  score: number; // Puntuación calculada: 0.60 * signing_bonus + 0.40 * annual_salary
  submitted_at: string;
  arrival: number; // Secuencia de llegada para desempate
  status: CoachOfferStatus;
}

/** Cuerpo en camelCase para PUT /offers/{id} */
export interface CoachOfferDto {
  signingBonus: number;
  annualSalary: number;
  releaseClause: number;
}

/** Entrenador congelado en el partido según match-engine-service-openapi.yaml */
export interface CoachMatchBonus {
  coachId: string | null;
  name: string | null;
  attack: number;
  midfield: number;
  defense: number;
}

/**
 * Determina si un contrato de entrenador está actualmente en vigor.
 * Se respeta terminated_at y ends_at además de active, ya que el proceso periódico
 * puede tardar unos instantes en actualizar el flag active.
 */
export function isCoachContractActive(contract: CoachContract | null | undefined): boolean {
  if (!contract) return false;
  if (!contract.active) return false;
  if (contract.terminated_at) return false;
  if (contract.ends_at) {
    const endsTime = new Date(contract.ends_at).getTime();
    if (!isNaN(endsTime) && endsTime <= Date.now()) {
      return false;
    }
  }
  return true;
}

/**
 * Calcula la puntuación oficial de una oferta de entrenador:
 * Puntuación = prima × 60% + salario de temporada × 40%
 */
export function calculateCoachOfferScore(signingBonus: number, annualSalary: number): number {
  const bonus = Number(signingBonus) || 0;
  const salary = Number(annualSalary) || 0;
  return Math.round((bonus * 0.60 + salary * 0.40) * 100) / 100;
}
